import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  Palette,
  Lock,
  ArrowRight,
  ExternalLink,
  Save,
  RotateCcw,
  Loader2,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { getVendorStatus } from '../../services/subscriptionService';
import { useVendorBranding, useUpdateVendorBranding } from '../../services/vendorBrandingService';
import { useToastContext } from '../../contexts/ToastContext';
import BrandImagePicker from '../../components/vendor/branding/BrandImagePicker';
import VendorBrandPreview from '../../components/vendor/branding/VendorBrandPreview';
import {
  DEFAULT_BRAND_SETTINGS,
  mergeBrandSettings,
  PRESET_COLORS,
} from '../../utils/vendorBranding';

const VendorCustomBranding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToastContext();
  const { hasActiveSubscription, isChecking } = useSubscriptionStatus();
  const [hasFeature, setHasFeature] = useState(false);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_BRAND_SETTINGS);
  const [profileMeta, setProfileMeta] = useState({
    profileId: null,
    businessName: '',
    city: '',
    state: '',
    logoUrl: null,
  });
  const [dirty, setDirty] = useState(false);

  const { data: brandingResponse, isLoading: brandingLoading } = useVendorBranding({
    enabled: hasActiveSubscription === true && hasFeature,
  });

  const updateMutation = useUpdateVendorBranding({
    onSuccess: () => {
      showSuccess('Saved', 'Your public profile branding has been updated.');
      setDirty(false);
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || 'Failed to save branding settings.';
      showError('Save failed', msg);
    },
  });

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (hasActiveSubscription === false) {
      navigate('/vendor/subscription', { replace: true });
      return;
    }
    if (hasActiveSubscription !== true) return;

    const fetchStatus = async () => {
      try {
        const response = await getVendorStatus();
        const features = response?.data?.current_subscription?.features || {};
        setHasFeature(!!features.feature_custom_branding);
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setFeatureLoading(false);
      }
    };
    fetchStatus();
  }, [user?.user_type, hasActiveSubscription, navigate]);

  useEffect(() => {
    if (!brandingResponse?.data) return;
    const data = brandingResponse.data;
    const brandSettings = mergeBrandSettings(data.brand_settings);
    // Account logo is canonical.
    brandSettings.logo_url = data.logo_url || brandSettings.logo_url || null;
    setSettings(brandSettings);
    setProfileMeta({
      profileId: data.profile_id,
      businessName: data.business_name || '',
      city: data.city || '',
      state: data.state || '',
      logoUrl: data.logo_url || brandSettings.logo_url || null,
    });
    setDirty(false);
  }, [brandingResponse]);

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const applyPreset = (preset) => {
    setSettings((prev) => ({
      ...prev,
      primary_color: preset.primary,
      accent_color: preset.accent,
    }));
    setDirty(true);
  };

  const handleReset = () => {
    if (brandingResponse?.data?.brand_settings) {
      const brandSettings = mergeBrandSettings(brandingResponse.data.brand_settings);
      brandSettings.logo_url =
        brandingResponse.data.logo_url || brandSettings.logo_url || null;
      setSettings(brandSettings);
    } else {
      setSettings(DEFAULT_BRAND_SETTINGS);
    }
    setDirty(false);
  };

  const handleSave = () => {
    // Never overwrite account logo from this page.
    const { logo_url: _ignoredLogo, ...payload } = settings;
    updateMutation.mutate(payload);
  };

  const publicProfileUrl = profileMeta.profileId ? `/vendor/${profileMeta.profileId}` : null;
  const accountLogoUrl = profileMeta.logoUrl || settings.logo_url || null;

  if (isChecking || featureLoading || (hasFeature && brandingLoading) || hasActiveSubscription !== true) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black" />
      </div>
    );
  }

  if (!hasFeature) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Custom Branding</h1>
          <p className="text-gray-600 mb-6">
            This feature is not included in your current plan. Upgrade to customize colors, cover, and hero style on your public vendor page. Upload your business logo anytime in Account.
          </p>
          <Link
            to="/vendor/subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            View Plans
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 bg-black rounded-xl flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Custom Branding</h1>
                <p className="text-sm text-gray-600">
                  Style your public profile — clients see this on the main website
                </p>
              </div>
            </div>
            {settings.enabled && (
              <p className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mt-2">
                <CheckCircle2 className="w-4 h-4" />
                Branding is live on your public profile
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {publicProfileUrl && (
              <Link
                to={publicProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              >
                <ExternalLink className="w-4 h-4" />
                View public page
              </Link>
            )}
            <button
              type="button"
              onClick={handleReset}
              disabled={!dirty || updateMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || updateMutation.isPending}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save changes
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6">
          {/* Editor */}
          <div className="space-y-5">
            {/* Enable toggle */}
            <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Publish branding</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    When enabled, your colors and assets replace the default look on{' '}
                    {publicProfileUrl ? (
                      <Link to={publicProfileUrl} className="underline hover:text-black">
                        your public vendor page
                      </Link>
                    ) : (
                      'your public vendor page'
                    )}
                    .
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={settings.enabled}
                  onClick={() => updateSetting('enabled', !settings.enabled)}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    settings.enabled ? 'bg-black' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                      settings.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </section>

            {/* Identity */}
            <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Identity</h2>
                <p className="text-sm text-gray-600 mt-1">Tagline shown on your public profile header.</p>
              </div>

              <div>
                <label htmlFor="tagline" className="block text-sm font-medium text-gray-900 mb-1.5">
                  Tagline
                </label>
                <input
                  id="tagline"
                  type="text"
                  maxLength={160}
                  value={settings.tagline || ''}
                  onChange={(e) => updateSetting('tagline', e.target.value || null)}
                  placeholder="e.g. Premium photo booth experiences for weddings & events"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                />
                <p className="text-xs text-gray-500 mt-1">{(settings.tagline || '').length}/160</p>
              </div>
            </section>

            {/* Colors */}
            <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Colors</h2>
                <p className="text-sm text-gray-600 mt-1">Primary color drives buttons; accent is used for highlights and banner gradients.</p>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Presets</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 text-xs hover:border-gray-400 bg-white"
                  >
                    <span className="flex -space-x-1">
                      <span className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: preset.primary }} />
                      <span className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: preset.accent }} />
                    </span>
                    {preset.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="primary_color" className="block text-sm font-medium text-gray-900 mb-1.5">
                    Primary color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      id="primary_color"
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="accent_color" className="block text-sm font-medium text-gray-900 mb-1.5">
                    Accent color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      id="accent_color"
                      type="color"
                      value={settings.accent_color}
                      onChange={(e) => updateSetting('accent_color', e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.accent_color}
                      onChange={(e) => updateSetting('accent_color', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Hero */}
            <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Hero & cover</h2>
                <p className="text-sm text-gray-600 mt-1">Choose how the top of your public profile is presented.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { id: 'classic', label: 'Classic', desc: 'Standard layout with gallery below the title' },
                  { id: 'banner', label: 'Banner', desc: 'Full-width cover image or gradient above content' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => updateSetting('hero_style', opt.id)}
                    className={`text-left p-4 rounded-lg border-2 transition-all ${
                      settings.hero_style === opt.id
                        ? 'border-black bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                  </button>
                ))}
              </div>

              {settings.hero_style === 'banner' && (
              <BrandImagePicker
                label="Cover image"
                value={settings.cover_image_url}
                onChange={(url) => updateSetting('cover_image_url', url)}
                hint="Recommended 1600×600px or wider. Cover uploads are kept separate from your portfolio gallery."
                uploadImageType="cover"
                galleryParams={{ non_portfolio: true }}
              />
              )}
            </section>

            {/* Logo comes from Account — show preview only */}
            <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Business logo</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Managed in Account. Changes there appear here and on invoices automatically.
                  </p>
                </div>
                <Link
                  to="/vendor/account?tab=business"
                  className="shrink-0 text-sm font-medium text-primary-600 hover:underline"
                >
                  Edit in Account
                </Link>
              </div>

              {accountLogoUrl ? (
                <div className="flex items-center gap-4">
                  <img
                    src={accountLogoUrl}
                    alt="Business logo"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200 bg-white"
                  />
                  <p className="text-sm text-gray-600">This logo is used on your public profile and receipts.</p>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-600">
                  No logo uploaded yet.{' '}
                  <Link to="/vendor/account?tab=business" className="text-primary-600 font-medium hover:underline">
                    Upload your logo in Account → Business Profile
                  </Link>
                  .
                </div>
              )}

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.show_logo_in_header !== false}
                  onChange={(e) => updateSetting('show_logo_in_header', e.target.checked)}
                  className="rounded border-gray-300 text-black focus:ring-black"
                />
                <span className="text-sm text-gray-700">Show logo in profile header</span>
              </label>
            </section>

            {/* CTA */}
            <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Booking button</h2>
                <p className="text-sm text-gray-600 mt-1">Customize the main call-to-action on your profile sidebar.</p>
              </div>
              <div>
                <label htmlFor="cta_label" className="block text-sm font-medium text-gray-900 mb-1.5">
                  Button label
                </label>
                <input
                  id="cta_label"
                  type="text"
                  maxLength={40}
                  value={settings.cta_label || ''}
                  onChange={(e) => updateSetting('cta_label', e.target.value || null)}
                  placeholder="View Services"
                  className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
            </section>
          </div>

          {/* Preview sidebar */}
          <div className="xl:sticky xl:top-6 xl:self-start space-y-4">
            <VendorBrandPreview
              businessName={profileMeta.businessName}
              brand={{ ...settings, logo_url: accountLogoUrl }}
              city={profileMeta.city}
              state={profileMeta.state}
            />
            <p className="text-xs text-gray-500 text-center px-2">
              Preview updates as you edit. Save to publish changes to your live public profile.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorCustomBranding;
